import React, { useState, useEffect, useRef } from "react";
import apiService from "../../services/api/api.service";
import { Edit2, Check, X, Loader } from "lucide-react";

const CompanyInformation = ({ uniqueTitle, onCompanyInfoUpdate }) => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    legalName: "",
    uniqueTitle: "",
    industry: "",
    sector: "",
    listingStatus: "",
    cin: "",
    pan: "",
    website: "",
    email: "",
    phone: "",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const modalRef = useRef(null);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const userInfo = await apiService.getUserInfo();
        setIsAdmin(userInfo?.role === "admin");
      } catch (err) {
        console.error("Failed to check admin status:", err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const result = await apiService.getProductByTitle(uniqueTitle);

        if (result.data && result.data.basicData) {
          setCompanyData(result.data.basicData);
          // Initialize form data with current values
          setUpdateFormData({
            legalName: result.data.basicData.legalName || "",
            uniqueTitle: result.data.basicData.uniqueTitle || "",
            industry: result.data.basicData.industry || "",
            sector: result.data.basicData.sector || "",
            listingStatus: result.data.basicData.listingStatus || "",
            cin: result.data.basicData.cin || "",
            pan: result.data.basicData.pan || "",
            website: result.data.basicData.website || "",
            email: result.data.basicData.email || "",
            phone: result.data.basicData.phone || "",
          });
        } else {
          throw new Error("Company data not found in API response");
        }
      } catch (err) {
        setError(
          err.message || "An error occurred while fetching company data"
        );
      } finally {
        setLoading(false);
      }
    };

    if (uniqueTitle) {
      fetchCompanyData();
    }
  }, [uniqueTitle]);

  // Handle clicks outside modal to close it
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowUpdateModal(false);
      }
    };

    if (showUpdateModal) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showUpdateModal]);

  const handleUpdateClick = () => {
    // Reinitialize form data from current company data to ensure form has latest values
    if (companyData) {
      setUpdateFormData({
        legalName: companyData.legalName || "",
        uniqueTitle: companyData.uniqueTitle || "",
        industry: companyData.industry || "",
        sector: companyData.sector || "",
        listingStatus: companyData.listingStatus || "",
        cin: companyData.cin || "",
        pan: companyData.pan || "",
        website: companyData.website || "",
        email: companyData.email || "",
        phone: companyData.phone || "",
      });
    }
    setShowUpdateModal(true);
    setUpdateSuccess(false);
    setUpdateError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateCompanyData = async (e) => {
    e.preventDefault();

    try {
      setUpdateLoading(true);
      setUpdateError(null);

      // Structure the update data according to your backend expectations
      const updateData = {
        basicData: {
          ...updateFormData,
        },
      };



      // Call the API endpoint to update the product
      const response = await apiService.updateProduct(uniqueTitle, updateData);


      if (response?.success) {
        setUpdateSuccess(true);

        // Update the local company data state with the new values
        setCompanyData(updateFormData);

        // Notify parent component about the update
        if (onCompanyInfoUpdate) {
          onCompanyInfoUpdate(updateFormData);
        }

        setTimeout(() => {
          setShowUpdateModal(false);
        }, 1500);
      } else {
        throw new Error(
          response?.message || "Failed to update company information"
        );
      }
    } catch (err) {
      setUpdateError(
        err.message ||
        "Failed to update company information."
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading)
    return (
      <div className="p-4 text-center text-gray-700 text-sm md:text-base">
        Loading company information...
      </div>
    );

  if (error)
    return (
      <div className="p-4 text-red-500 text-center text-sm md:text-base">
        Error: {error}
      </div>
    );

  return (
    <div className="p-4 sm:p-6 mb-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Company Information</h2>

        <button
          onClick={handleUpdateClick}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Edit2 size={16} /> Edit Information
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Column 1 */}
        <div className="space-y-4">
          <InfoItem
            label="Legal Name"
            value={companyData?.legalName || "N/A"}
          />
          <InfoItem
            label="Unique Identifier"
            value={companyData?.uniqueTitle || "N/A"}
          />
          <InfoItem label="Industry" value={companyData?.industry || "N/A"} />
          <InfoItem label="Sector" value={companyData?.sector || "N/A"} />
          <InfoItem
            label="Listing Status"
            value={companyData?.listingStatus || "N/A"}
          />
        </div>

        {/* Column 2 */}
        <div className="space-y-4">
          <InfoItem label="CIN" value={companyData?.cin || "N/A"} />
          <InfoItem label="PAN" value={companyData?.pan || "N/A"} />
          <InfoItem
            label="Website"
            value={
              companyData?.website ? (
                <a
                  href={companyData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-words"
                >
                  {companyData.website}
                </a>
              ) : (
                "N/A"
              )
            }
          />
          <InfoItem label="Contact Email" value={companyData?.email || "N/A"} />
          <InfoItem label="Contact Phone" value={companyData?.phone || "N/A"} />
        </div>
      </div>

      {/* Update Company Information Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Update Company Information
              </h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {updateSuccess ? (
              <div className="bg-green-100 text-green-700 p-3 rounded mb-4">
                Company information updated successfully!
              </div>
            ) : (
              <form onSubmit={handleUpdateCompanyData}>
                {updateError && (
                  <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
                    {updateError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Legal Name
                    </label>
                    <input
                      type="text"
                      name="legalName"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.legalName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Unique Identifier
                    </label>
                    <input
                      type="text"
                      name="uniqueTitle"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.uniqueTitle}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Industry
                    </label>
                    <input
                      type="text"
                      name="industry"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.industry}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Sector
                    </label>
                    <input
                      type="text"
                      name="sector"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.sector}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Listing Status
                    </label>
                    <select
                      name="listingStatus"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.listingStatus}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Suspended">Suspended</option>
                      <option value="Delisted">Delisted</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      CIN
                    </label>
                    <input
                      type="text"
                      name="cin"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.cin}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      PAN
                    </label>
                    <input
                      type="text"
                      name="pan"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.pan}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.website}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={updateFormData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center gap-2"
                    onClick={() => setShowUpdateModal(false)}
                  >
                    <X size={16} /> Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        {" "}
                        <Loader
                          className="animate-spin text-indigo-500 mb-4"
                          size={36}
                        />
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Update Information
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable component for info items
const InfoItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs sm:text-sm text-gray-500 mb-1">{label}</span>
    <span className="font-medium text-sm sm:text-base text-gray-900">
      {value}
    </span>
  </div>
);

export default CompanyInformation;
